import { ProductNewModel } from "../../models/products/product-new-model";
import React, { useEffect, useRef, useState } from "react";
import ProductService from "../../services/products/products.service";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Helpers from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import { InvoiceProduct } from "../../models/invoice/invoice-product-insert-model";
import { Toast, ToastMessage } from "primereact/toast";
import ProductInvoice from "../../services/invoices/gen_inv.service";
import AppLoading from '../app-loading/app-loading';
import { InputText } from "primereact/inputtext";
import { OrderDetail } from "../../models/orders/order-list-model";

export const NewAddProduct = ({ orderedItems, updateItems }: any) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<ProductNewModel[]>([]);
  const [quantities, setQuantities] = useState<{[key: number]: number}>({});
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState<string>("");

  const showMessage = (
    label: string,
    message: string,
    severity: ToastMessage["severity"]
  ) => {
    toast.current?.show({
      severity: severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const imageBodyTemplate = (item: any) => {
    return (
      <img
        src={
          item.media[0] !== undefined
            ? item.media[0].url
            : process.env.PUBLIC_URL +
              "/assests/images/Desiri-Groundnut-Oils.png"
        }
        alt=""
        width="90%"
      />
    );
  };

  const addProductBtn = (product: any) => {
    return (
      <Button label="Add" onClick={() => handleAdd(product.id)} />
    );
  };

  const unitPriceBodyTemplate = (product: any) => {
    return Helpers.formatAmountInINR(product.unitPrice);
  };

  const quantityTemplate = (product: any) => {
    return (
      <div className="flex product-item product-wrapper">
        <div className="product-list-quantity">
          <InputNumber
            inputId={String(product.id)}
            style={{ width: 120 }}
            value={quantities[product.id] || product.minOrderQty}
            showButtons
            buttonLayout="horizontal"
            step={product.orderMultiples}
            decrementButtonClassName="p-button-danger"
            className="quantity-border"
            incrementButtonClassName="p-button-success"
            incrementButtonIcon="pi pi-plus"
            decrementButtonIcon="pi pi-minus"
            min={product.minOrderQty}
            onChange={(e) => setQuantities(prev => ({
              ...prev,
              [product.id]: e.value as number
            }))}
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    ProductService.getAllProducts()
      .then((response) => {
        setProducts(response);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
        setLoading(false);
      });
  }, []);

  const handleAdd = async (id: number) => {
    setLoading(true);
    
    const productItem = products.find((x: any) => x.id === id);
    if (!productItem) {
      setLoading(false);
      return;
    }

    const qty = quantities[id] || productItem.minOrderQty;
    const taxAmount = (productItem.unitPrice * qty) * (productItem.tax / 100);
    const taxableAmount = productItem.unitPrice * qty;

    const updatedInvoiceProduct: InvoiceProduct = {
      OrderID: orderedItems.orderID,
      ProductID: productItem.id,
      Quantity: qty,
      OrderStatusID: orderedItems.orderStatusID,
      UnitPrice: productItem.unitPrice,
      PendingQuantity: qty,
      TaxableAmount: taxableAmount,
      TaxAmount: taxAmount,
      DiscountAmount: orderedItems.discountAmount,
      TrackingID: '1',
      CourierDocketNbr: '',
      GrossAmount: taxableAmount + taxAmount,
      ModifiedBy: orderedItems.userID,
      ModifiedDate: orderedItems.modifiedDate,
      OrderGrossAmount: orderedItems.grossAmount + taxableAmount + taxAmount,
      OrderTaxableAmount: orderedItems.taxableAmount + taxableAmount,
      OrderTaxAmount: orderedItems.taxAmount + taxAmount
    };

    try {
      const response = await ProductInvoice.InsertProductToInvoice(updatedInvoiceProduct);
      
      if (response.isSuccess) {
        showMessage("Add Products", "Product Added successfully", "success");

        const updatedOrderInvoiceDetails: OrderDetail = {
          orderDetailID: response.id,
          orderId: orderedItems.orderID,
          productID: productItem.id,
          productName: productItem.name,
          categoryName: productItem.categoryName,
          subCategoryName: productItem.subCategoryName,
          hsN_Code: '',
          orderStatusName: orderedItems.orderStatusName,
          measurementTypeName: '',
          unitPrice: productItem.unitPrice,
          minOrderQty: productItem.minOrderQty,
          orderMultiples: productItem.orderMultiples,
          quantity: qty,
          pendingQuantity: qty,
          taxableAmount: taxableAmount,
          gst: productItem.tax,
          taxAmount: taxAmount,
          grossAmount: taxableAmount + taxAmount,
          url: productItem.media[0]?.url || '',
          trackingID: '1'
        };
        
        const updatedItems = {
          ...orderedItems,
          orderDetails: [...(orderedItems.orderDetails || []), updatedOrderInvoiceDetails],
          products: [...(orderedItems.products || []), {
            productID: productItem.id,
            productName: productItem.name,
            pendingQuantity: qty,
            unitPrice: productItem.unitPrice,
            gst: productItem.tax,
            taxableAmount: taxableAmount,
            taxAmount: taxAmount,
            grossAmount: taxableAmount + taxAmount,
            productImageURL: productItem.media[0]?.url || '',
            orderMultiples: productItem.orderMultiples,
            minOrderQty: productItem.minOrderQty
          }],
          grossAmount: orderedItems.grossAmount + taxableAmount + taxAmount,
          taxAmount: orderedItems.taxAmount + taxAmount,
          taxableAmount: orderedItems.taxableAmount + taxableAmount
        };
        
        updateItems(updatedItems);
      } else {
        showMessage("Add Products", response.message, "error");
      }
    } catch (error) {
      console.error("Error adding product:", error);
      showMessage("Add Products", "Failed to add product", "error");
    } finally {
      setLoading(false);
      setQuantities(prev => {
        const newQuantities = {...prev};
        delete newQuantities[id];
        return newQuantities;
      });
    }
  };

  const filteredData = products.filter((item) =>
    item.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="col-12">
      <Toast ref={toast} position="top-right" />
      <div className="col-12">
        <div className="flex align-items-center flex-wrap gap-2">
          <div className="font-bold text-white border-round p-input-icon-left" style={{float:"right"}}> 
            <i className="fa-solid fa-search"></i>
            <InputText
              id="search"
              name="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t("common_search_placeholder")}
            />
          </div> 
        </div>
      </div>
      <DataTable 
        value={filteredData}   
        tableStyle={{ minWidth: '50rem' }}
        globalFilterFields={['name']}
      >      
        <Column style={{ width: "10%", marginLeft: "8rem" }} body={imageBodyTemplate}></Column>
        <Column
          field="name"
          style={{ width: "50%" }}
          header={t("invoices_product_name")}
        ></Column>        
        <Column
          field="unitPrice"
          header={t("invoices_product_price")}
          body={unitPriceBodyTemplate}
        ></Column>
        <Column
          field="quantity"
          header={t("invoices_product_quantity")}
          body={quantityTemplate}
        ></Column>
        <Column field="id" header="Action" body={addProductBtn}></Column>
      </DataTable>
      {loading && <AppLoading />}
    </div> 
  );
};